import { AxiosError } from 'axios'
import apiClient from '@app/apiClient'
import authLogger from './auth-logger'

const userBackendApi = apiClient(IDENTITY_SERVICE_URL)
const lenderBackendApi = apiClient(API2_URL)

/**
 * Used when the auth config request fails
 */
export const FALLBACK_AUTH_CONFIG = {
  use_session_flow: false,
  use_login_v2: true,
}

export const userServiceEndpoints = {
  LOGIN: async (username, password, lender_id) => {
    try {
      if (!lender_id) {
        throw new Error('Account selection required')
      }
      if (!username || !password) {
        throw new Error('Username and password are required')
      }
      const formData = new FormData()
      formData.append('username', username)
      formData.append('password', password)
      formData.append('lender_id', lender_id)

      const loginResult = await userBackendApi.postForm('/lender/auth/login', {}, formData, {
        withCredentials: true,
      })
      return loginResult
    } catch (error) {
      if (error?.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw error
    }
  },
  EXCHANGE_LOGIN_CODE: async (loginCode) => {
    try {
      if (!loginCode) {
        throw new Error('Login code is required')
      }
      const formData = new FormData()
      formData.append('code', loginCode)

      const codeResult = await userBackendApi.postForm('/lender/auth/code', {}, formData, {
        withCredentials: true,
      })
      return codeResult
    } catch (error) {
      if (error?.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw error
    }
  },
  LOGOUT: async () => {
    try {
      await userBackendApi.post('/lender/auth/logout', {}, {}, { withCredentials: true })
    } catch (error) {
      if (error?.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw error
    }
  },
  TOKEN: async () => {
    try {
      const tokenResult = await userBackendApi.post('/lender/auth/token', {}, {}, { withCredentials: true })
      return tokenResult
    } catch (error) {
      if (error?.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw error
    }
  },
  ACCOUNT_OPTIONS: async (email) => {
    try {
      const lenderAccounts = await userBackendApi.post('/lender/accounts', {}, { email: email }, { withCredentials: false })
      return lenderAccounts
    } catch (error) {
      if (error?.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw error
    }
  },
  ACCOUNT_LOGIN_METHODS: async (accountId) => {
    try {
      const lenderLoginMethods = await userBackendApi.post(`/lender/accounts/${accountId}/login_methods`, {}, {}, { withCredentials: false })
      return lenderLoginMethods
    } catch (error) {
      if (error?.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw error
    }
  },
}

export const lenderBackendEndpoints = {
  LOGIN: async (username, password, lender_id) => {
    try {
      // TODO: PLAT-2901 - Fix account selection so we always have lender_id
      // if (!lender_id) {
      //   throw new Error('Account selection required')
      // }
      if (!username || !password) {
        throw new Error('Username and password are required')
      }
      const queryParams = lender_id ? { lender: lender_id } : {}
      const result = await lenderBackendApi.post('/auth', queryParams, { username, password })
      return result
    } catch (error) {
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },
  LOGOUT: async () => {
    try {
      await lenderBackendApi.post('/auth/logout')
    } catch (error) {
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },
  CURRENT_USER: async () => {
    try {
      const result = await lenderBackendApi.get('/users/current_user')
      return result
    } catch (error) {
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },
}

export const authConfigEndpoints = {
  GET_CONFIG: async () => {
    try {
      const result = await userBackendApi.get('/lender/auth/config', {}, { timeout: 500 })
      return result
    } catch (error) {
      if (error?.response?.data?.detail) {
        authLogger.error('config request failed:', error.response.data.detail)
        return FALLBACK_AUTH_CONFIG
      }

      if (error?.code !== AxiosError.ECONNABORTED) {
        authLogger.error('config request failed:', error)
      }
      return FALLBACK_AUTH_CONFIG
    }
  },
}
